import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import InteractiveSimulationMap from '../Simulations/InteractiveSimulationMap';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import HydrodigitalLogo from '@/components/General/HydrodigitalLogo';
import { Button, notification } from 'antd';
import InfrastructureSelector from './InfrastructureSelector';
import './index.scss';
import TimelineChart from '../../components/InteractiveSimulation/TimelineChart';
import IterationSimulationService from '@/domain/IterationSimulation/IterationSimulationService';
import { useDispatch } from 'react-redux';
import { setLoaderText } from '../../redux/slices/loaderSlice';
import { interactiveSimulationsEventsService } from '@/events/interactiveSimulations';
import InteractiveSimulationService from '@/domain/InteractiveSimulations/InteractiveSimulationService';

import { ReplayInteractiveContentChartPlayer } from '@/components/InteractiveSimulation/InteractiveSimulationCardContent';

// Constantes
const SIMULATION_INTERVAL = 1000;
const PAUSE_DELAY = 1500;

const interactiveSimulationService = new InteractiveSimulationService();

export default function InteractiveSimulationReplayV2() {
	const subscriptionIS$ = useMemo(() => interactiveSimulationsEventsService.getSubject(), []);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { simulationId, locationId } = useParams();

	//Iteration Data
	const [iterationList, setIterationList] = useState([]);
	const [currentIteration, setCurrentIteration] = useState(null);

	//TimePlayer/Socket/Simulation
	const [isRunningIteration, setIsRunningIteration] = useState(false);
	const [isWaitingResponse, setIsWaitingResponse] = useState(false);
	const [eventsSent, setEventsSent] = useState([]);
	const [currentTime, setCurrentTime] = useState(null);
	const [interactiveSimulationData, setInteractiveSimulationData] = useState(null);
	const [playing, setPlaying] = useState(false);
	const [markers, setMarkers] = useState([]);
	const [replayCompleted, setReplayCompleted] = useState(false);

	//Replay Data
	const [replayData, setReplayData] = useState([]);
	const intervalSimulation = useRef(null);
	const replayIndex = useRef(0);

	//Parameters
	const [infraestructures, setInfraestructures] = useState([]);

	const [selectedParameters, setSelectedParameters] = useState([]);


	const [leftPanelHeight, setLeftPanelHeight] = useState('58vh');

	
	const calculateLeftPanelHeight = useCallback(() => {
		const windowHeight = window.innerHeight;
		const headerHeight = 70;
		const drawerPadding = 32;
		const timelinePlayerHeight = 50;
		const chartHeight = 160; 
		const drawerHeaderHeight = 50; 
		const marginBottom = 30; 
		
		
		let drawerContentHeight = drawerPadding + drawerHeaderHeight;
		
		if (selectedParameters?.length === 0) {
			
			drawerContentHeight += 100; 
		} else {
			
			drawerContentHeight += timelinePlayerHeight + chartHeight + 10; 
		}
		
		
		const availableHeight = windowHeight - headerHeight - drawerContentHeight - marginBottom;
		const maxHeight = Math.max(280, availableHeight); 
		
		setLeftPanelHeight(`${maxHeight}px`);
	}, [selectedParameters]);

	
	useEffect(() => {
		calculateLeftPanelHeight();
		
		const handleResize = () => {
			calculateLeftPanelHeight();
		};
		
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [calculateLeftPanelHeight]);

	const addMarker = useCallback(
		(events, eventTime = null) => {
			const markerTime = eventTime ? dayjs(eventTime) : dayjs(currentTime);

			const newMarker = {
				time: markerTime,
				events,
			};
			setMarkers((prevMarkers) => {
				const updatedMarkers = [...prevMarkers, newMarker];
				updatedMarkers.sort((a, b) => a.time - b.time);
				return updatedMarkers;
			});
		},
		[currentTime],
	);

	// Helper function to map infrastructureId to infraName using selectedParameters
	const getInfraNameById = useCallback(
		(infrastructureId) => {
			const parameter = selectedParameters.find(
				(param) =>
					param.infrastructureId === String(infrastructureId) ||
					param.infrastructureId === infrastructureId,
			);
			return parameter ? parameter.infraestructureName : `Infrastructure ${infrastructureId}`;
		},
		[selectedParameters],
	);

	const cancelSimulation = useCallback(() => {
		navigate('/simulations');
	}, [navigate]);

	const mapIteration = useCallback(
		(iteration) => ({
			iterationId: iteration.id,
			ended: true,
			markers: iteration.infraEvents.reduce((accumulator, currentValue) => {
				return [...accumulator, ...currentValue.events];
			}, []),
		}),
		[],
	);

	const getDataOfAnIteration = useCallback(
		async (iterationId) => {
			try {
				const res = await interactiveSimulationService.getDataOfAnIteration(
					simulationId,
					iterationId,
				);
				const data = res
					.filter((it) => it.partial_request.status === 'RUNNING')
					.map((iterationData) => ({
						data: iterationData.partial_response,
						requestData: iterationData.partial_request.events,
						timeEvent: iterationData.partial_request.timeEvent,
						time: dayjs(iterationData.time_response),
					}));

				setReplayData(data);
			} catch (error) {
				console.error('Error fetching iteration data:', error);
				setReplayData([]);
			}
		},
		[simulationId],
	);

	const selectLastIteration = useCallback(
		(iterations) => {
			if (iterations.length > 0) {
				const firstIteration = iterations[iterations.length - 1];
				setCurrentIteration(firstIteration);
				getDataOfAnIteration(firstIteration.iterationId);
			}
		},
		[getDataOfAnIteration],
	);

	const getIterations = useCallback(async () => {
		try {
			const iterationList =
				await interactiveSimulationService.getAllIterationsInteractiveSimulation(simulationId);
			const mappedIterations = iterationList.map(mapIteration);

			setIterationList(mappedIterations);
			selectLastIteration(mappedIterations);
		} catch (error) {
			console.error('Error fetching iterations:', error);
			setIterationList([]);
		}
	}, [simulationId, mapIteration, selectLastIteration]);

	const getSimulationInfo = useCallback(async () => {
		try {
			const res = await interactiveSimulationService.getSimulationInfo(simulationId);
			setInteractiveSimulationData({
				simulationName: res.name,
				startDate: res.start_date,
				endDate: res.end_date,
				step: res.step,
			});
			setCurrentTime(dayjs(res.start_date));
		} catch (error) {
			console.error('Error fetching simulation info:', error);
		}
	}, [simulationId]);

	const processReplayData = useCallback(
		(replayItem, localOffset) => {
			const adjustedTime = dayjs(replayItem.time).subtract(localOffset, 'ms');
			setCurrentTime(adjustedTime);

			// Filter data based on selected parameters if any are selected
			const selectedParamIds = selectedParameters.map((param) => String(param.paremeterId));
			const currentData = replayItem.data || [];

			let dataToGraph;
			if (selectedParameters.length > 0) {
				const filteredData = currentData.filter((rd) =>
					selectedParamIds.includes(String(rd.paramId)),
				);
				dataToGraph = filteredData.map((rd) => ({
					paramId: rd.paramId,
					value: rd.value,
					time: adjustedTime,
				}));
			} else {
				// If no parameters selected, show all data
				dataToGraph = currentData.map((rd) => ({
					paramId: rd.paramId,
					value: rd.value,
					time: adjustedTime,
				}));
			}

			// Add markers for events if requestData exists
			const requestData = replayItem.requestData;
			const timeEvent = replayItem.timeEvent;
			if (requestData && requestData.length > 0) {
				const eventsForMarker = requestData.map((event) => ({
					infrastructureId: event.infrastructureId,
					infraName: getInfraNameById(event.infrastructureId),
					mod_value: event.mod_value,
				}));
				addMarker(eventsForMarker, timeEvent);
			}

			interactiveSimulationsEventsService.setSubject({
				type: 'dataSocketRecived',
				data: dataToGraph,
				currentTime: adjustedTime, // Pass the exact time for synchronization
			});
		},
		[selectedParameters, getInfraNameById, addMarker],
	);

	const finishReplay = useCallback((intervalId) => {
		setIsRunningIteration(false);
		setIsWaitingResponse(false);
		setPlaying(false);
		setReplayCompleted(true);
		clearInterval(intervalId);
		interactiveSimulationsEventsService.setSubject({
			type: 'resetPlaying',
			data: false,
		});
		interactiveSimulationsEventsService.setSubject({
			type: 'replayCompleted',
			data: true,
		});
	}, []);

	const restartReplay = useCallback(() => {
		setReplayCompleted(false);
		setCurrentTime(null);
		replayIndex.current = 0;
		interactiveSimulationsEventsService.setSubject({
			type: 'resetGraph',
			isRestart: true,
		});
		setPlaying(true);
	}, []);

	const replaySimulation = useCallback(() => {
		// Clear any existing interval
		if (intervalSimulation.current) {
			clearInterval(intervalSimulation.current);
		}

		// Solo limpiar markers si es un restart (índice = 0)
		if (replayIndex.current === 0) {
			setMarkers([]);
		}

		// Ensure we have data to replay
		if (!replayData || replayData.length === 0) {
			console.warn('No replay data available');
			return;
		}

		setIsRunningIteration(true);

		const localOffset = new Date().getTimezoneOffset() * 60 * 1000;

		const intervalReplay = setInterval(() => {
			// Check if replay should continue
			if (!playing) {
				clearInterval(intervalReplay);
				intervalSimulation.current = null;
				setIsRunningIteration(false);
				return;
			}

			if (replayIndex.current >= replayData.length) {
				finishReplay(intervalReplay);
			} else {
				processReplayData(replayData[replayIndex.current], localOffset);
				replayIndex.current++;

				// If this was the last point, allow it to render before finishing
				if (replayIndex.current >= replayData.length) {
					setTimeout(() => {
						finishReplay(intervalReplay);
					}, 100); // Short delay to ensure last point renders
					return; // Exit interval to avoid double finishReplay call
				}
			}
		}, SIMULATION_INTERVAL);

		intervalSimulation.current = intervalReplay;
	}, [replayData, processReplayData, finishReplay, playing]);

	useEffect(() => {
		return () => {
			if (intervalSimulation.current) {
				clearInterval(intervalSimulation.current);
			}
		};
	}, []);

	useEffect(() => {
		getIterations();
		getSimulationInfo();
	}, [getIterations, getSimulationInfo]);

	useEffect(() => {
		if (playing && !replayCompleted) {
			// Solo resetear gráfico si es un restart (índice = 0)
			if (replayIndex.current === 0) {
				interactiveSimulationsEventsService.setSubject({
					type: 'resetGraph',
					isRestart: true,
				});
			}
			replaySimulation();
		} else if (!playing && intervalSimulation.current) {
			// Pausar: limpiar el intervalo pero mantener el estado y datos del gráfico
			clearInterval(intervalSimulation.current);
			intervalSimulation.current = null;
			setIsRunningIteration(false);
			// No enviar resetGraph en pausa para mantener contenido visible
		}
	}, [playing, replaySimulation, replayCompleted]);

	useEffect(() => {
		const subscription = subscriptionIS$.subscribe((event) => {
			if (event.type === 'pauseSimulation' && isRunningIteration) {
				setIsWaitingResponse(true);
				setTimeout(() => {
					setIsWaitingResponse(false);
					setPlaying(false);
				}, PAUSE_DELAY);
			}
		});

		return () => subscription.unsubscribe();
	}, [subscriptionIS$, isRunningIteration]);

	useEffect(() => {
		const subscription = subscriptionIS$.subscribe((event) => {
			if (event.type === 'playSimulation') {
				setPlaying(true);
				setReplayCompleted(false);
			} else if (event.type === 'pauseSimulation') {
				setPlaying(false);
			} else if (event.type === 'setCurrentIteration') {
				// Reset states when changing iterations
				setCurrentIteration(event.data);
				setReplayCompleted(false);
				setCurrentTime(null);
				replayIndex.current = 0;
				setMarkers([]);
				getDataOfAnIteration(event.data.iterationId);
			}
		});

		return () => subscription.unsubscribe();
	}, [subscriptionIS$, getDataOfAnIteration]);

	const handleSelectedParametersChange = (parameters) => {
		setSelectedParameters(parameters);
	};

	return (
		<>
			<div className="header-interactiveSimulation">
				<HydrodigitalLogo
					onClick={() => {
						// navigate('/');
					}}
					size="lg"
				/>
				<div className="vertical-separator" />
				<Button color="primary" variant="outlined" shape="round" onClick={cancelSimulation}>
					Back to Simulations
				</Button>
			</div>
			<div
				style={{
					position: 'fixed',
					left: '10px',
					top: '70px',
					width: '350px',
					height: leftPanelHeight,
					maxHeight: leftPanelHeight,
					backgroundColor: 'white',
					border: '1px solid #d9d9d9',
					borderRadius: '6px',
					zIndex: 1000,
					overflow: 'hidden',
				}}
			>
				<InfrastructureSelector
					locationId={locationId}
					onSelectedParameters={handleSelectedParametersChange}
					initialSelectedParameters={selectedParameters}
				/>
			</div>
			<InteractiveSimulationMap
				infrasEdited={[]}
				setInfraestructures={setInfraestructures}
				eventsSended={eventsSent}
				iterationId={currentIteration?.iterationId}
				lastTimeReceived={null}
				isWaitingResponse={isWaitingResponse}
				isPaused={!playing}
			/>
			{interactiveSimulationData && currentIteration ? (
				// <TimelineChart
				// 	parameters={selectedParameters}
				// 	startDate={dayjs(interactiveSimulationData.startDate)}
				// 	endDate={dayjs(interactiveSimulationData.endDate)}
				// 	simulationName={interactiveSimulationData.simulationName}
				// 	currentTime={currentTime}
				// 	events={markers}
				// 	iterations={iterationList}
				// 	currentIteration={currentIteration}
				// 	isWaitingResponse={isWaitingResponse}
				// 	isReplay={true}
				// 	replayCompleted={replayCompleted}
				// 	onRestart={restartReplay}
				// 	isPlaying={playing}
				// />
				<ReplayInteractiveContentChartPlayer
					title={interactiveSimulationData.simulationName}
					chartStartDate={dayjs(interactiveSimulationData.startDate)}
					chartEndDate={dayjs(interactiveSimulationData.endDate)}
					parameters={selectedParameters}
					iterations={iterationList}
					currentIteration={currentIteration}
					dataReplayInteractive={replayData}
					infraestructures={infraestructures}
				/>
			) : (
				<div className="flex justify-center align-center">
					<span style={{ fontSize: '1rem' }}>No iteration selected.</span>
				</div>
			)}
		</>
	);
}
